import { TrackerDataType } from '../constants/constants';
import { IssueKeys } from '../constants/IssueAndServiceKeys';
import dayjs from '../../utils/dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createInteraction, deleteInteraction, updateInteractionWithFiles } from '../requests/interactionsRequests';
import { duplicateMediaInteraction } from '../../components/TrackerTable/trackerTableRequest';
import { normalizeId, normalizeLeadIds } from '../utils/mediaInteractionNormalization';

export const useMediaInteractionHandler = ({ recentCreations, onSuccessCallback }) => {
  const queryClient = useQueryClient();
  const invalidateDropdownTable = async () => {
    await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
  };
  const toLinkedIds = (linkedItems) => {
    if (!linkedItems) {
      return [];
    }
    const items = Array.isArray(linkedItems) ? linkedItems : [linkedItems];
    return items.map((item) => item?._id ?? item?.id ?? item).filter(Boolean);
  };
  const invalidateLinkedServiceDetails = async (linkedItems) => {
    await Promise.all(
      toLinkedIds(linkedItems).map((serviceId) =>
        queryClient.invalidateQueries({ queryKey: ['serviceDetail', serviceId] })
      )
    );
  };

  const normalizeStringArray = (value, { allowNull = false } = {}) => {
    if (value === null) {
      return allowNull ? null : [];
    }
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item?.name) {
          return item.name;
        }
        if (item?._id) {
          return item._id;
        }
        if (item?.id) {
          return item.id;
        }
        return null;
      })
      .filter(Boolean);
  };

  const normalizeIdArray = (value) => {
    if (value === null) {
      return [];
    }
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list.map((item) => normalizeId(item)).filter(Boolean);
  };

  const normalizeTypes = (value, { requireId = false } = {}) => {
    if (value === null) {
      return [];
    }
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list
      .map((item) => {
        if (!item || typeof item === 'string') {
          return null;
        }
        const typeId = normalizeId(item);
        if (requireId && !typeId) {
          return null;
        }
        const normalized = {
          _id: typeId,
          name: item.name,
          type: item.type,
          backgroundColor: item.backgroundColor,
          permissions: item.permissions,
          proactivity: item.proactivity,
          archived: item.archived,
        };
        if (!normalized.name || !normalized.type) {
          return null;
        }
        if (!normalized._id) {
          delete normalized._id;
        }
        return normalized;
      })
      .filter(Boolean);
  };

  const normalizeCampaigns = (value, { requireId = false } = {}) => {
    if (value === null) {
      return [];
    }
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list
      .map((item) => {
        if (!item || typeof item === 'string') {
          return null;
        }
        const campaignId = normalizeId(item);
        if (requireId && !campaignId) {
          return null;
        }
        const normalized = {
          _id: campaignId,
          name: item.name,
          backgroundColor: item.backgroundColor,
          archived: item.archived,
        };
        if (!normalized.name) {
          return null;
        }
        if (!normalized._id) {
          delete normalized._id;
        }
        return normalized;
      })
      .filter(Boolean);
  };

  const normalizeLinkedServices = (value, { forUpdate = false } = {}) => {
    if (value === null) {
      return forUpdate ? null : [];
    }
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list
      .map((item) => {
        if (!item) {
          return null;
        }
        if (typeof item === 'string') {
          return forUpdate ? { _id: item, name: '' } : item;
        }
        const serviceId = normalizeId(item);
        if (!serviceId) {
          return null;
        }
        const name = item.name ?? item.service ?? item.title;
        return forUpdate ? { _id: serviceId, name: name ?? '' } : { _id: serviceId, name };
      })
      .filter(Boolean);
  };

  const normalizeFieldAddons = (value) => {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const normalized = {};
    if (value.keyMessaging !== undefined) {
      normalized.keyMessaging = value.keyMessaging;
    }
    if (value.response !== undefined) {
      normalized.response = value.response;
    }
    return Object.keys(normalized).length > 0 ? normalized : undefined;
  };

  const normalizeFiles = (value) => {
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value) {
      list = [value];
    }
    return list
      .map((file) => {
        if (!file || typeof file !== 'object') {
          return null;
        }
        const normalized = {
          _id: file._id,
          type: file.type,
          name: file.name,
          description: file.description,
          path: file.path,
        };
        if (!normalized.type || !normalized.name || !normalized.path) {
          return null;
        }
        if (!normalized._id) {
          delete normalized._id;
        }
        if (!normalized.description) {
          delete normalized.description;
        }
        return normalized;
      })
      .filter(Boolean);
  };

  const normalizeMediaInteractionFields = (fields, { isUpdate = false } = {}) => {
    if (!fields || typeof fields !== 'object') {
      return {};
    }
    const normalized = {};
    const hasField = (key) => Object.hasOwn(fields, key);

    if (!isUpdate) {
      if (hasField('date')) {
        normalized.date = fields.date;
      }
      if (hasField('year')) {
        normalized.year = fields.year;
      }
      if (hasField('month')) {
        normalized.month = fields.month;
      }
    }

    if (hasField('entryDate')) {
      normalized.entryDate = fields.entryDate;
    }
    if (hasField('dueDate')) {
      normalized.dueDate = fields.dueDate;
    }
    if (hasField('contactMethod')) {
      normalized.contactMethod = fields.contactMethod;
    }
    if (hasField('topic')) {
      normalized.topic = fields.topic;
    }
    if (hasField('response')) {
      normalized.response = fields.response;
    }
    if (hasField('keyMessaging')) {
      normalized.keyMessaging = fields.keyMessaging;
    }
    if (hasField('status')) {
      normalized.status = fields.status;
    }
    if (hasField('tone')) {
      normalized.tone = fields.tone ?? null;
    }
    if (hasField('units')) {
      normalized.units = normalizeStringArray(fields.units, { allowNull: isUpdate });
    }
    if (hasField('dept')) {
      if (fields.dept === null && isUpdate) {
        normalized.dept = null;
      } else {
        normalized.dept = normalizeStringArray(fields.dept);
      }
    }
    if (hasField('type')) {
      normalized.type = normalizeTypes(fields.type, { requireId: isUpdate });
    }
    if (hasField('expert')) {
      normalized.expert = normalizeIdArray(fields.expert);
    }
    if (hasField('campaign')) {
      normalized.campaign = normalizeCampaigns(fields.campaign, { requireId: isUpdate });
    }
    if (hasField('journalist')) {
      const journalistId = normalizeId(fields.journalist);
      normalized.journalist = journalistId ?? (isUpdate ? null : undefined);
    }
    if (hasField('outlet')) {
      const outletId = normalizeId(fields.outlet);
      normalized.outlet = outletId ?? (isUpdate ? null : undefined);
    }
    if (hasField('lead')) {
      normalized.lead = isUpdate ? normalizeLeadIds(fields.lead) : normalizeIdArray(fields.lead);
    }
    if (hasField('linkedService')) {
      normalized.linkedService = normalizeLinkedServices(fields.linkedService, { forUpdate: isUpdate });
    }
    if (isUpdate && hasField('fieldAddons')) {
      const fieldAddons = normalizeFieldAddons(fields.fieldAddons);
      if (fieldAddons !== undefined) {
        normalized.fieldAddons = fieldAddons;
      }
    }
    if (!isUpdate && hasField('file')) {
      normalized.file = normalizeFiles(fields.file);
    }

    return normalized;
  };

  const { markCreated, isNewlyAdded } = recentCreations;

  const createInteractionMutation = useMutation({
    mutationFn: async ({ fields, files }) => {
      const payload = { ...normalizeMediaInteractionFields(fields, { isUpdate: false }) };
      if (files?.length) {
        payload.file = normalizeFiles(files);
      }
      const data = await createInteraction(payload);
      return data;
    },
    onSuccess: async (data, variables) => {
      await invalidateDropdownTable();
      if (data?.id) {
        await invalidateLinkedServiceDetails(variables?.fields?.linkedService);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const duplicateInteractionMutation = useMutation({
    mutationFn: async (id) => {
      const data = await duplicateMediaInteraction(id);
      return data;
    },
    onSuccess: async ({ id }) => {
      if (id) {
        markCreated(id);
      }
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const updateInteractionWithFileMutation = useMutation({
    mutationFn: async ({ id, fields, uploadingUrls, removingUrls }) => {
      const data = await updateInteractionWithFiles({
        id,
        fields: normalizeMediaInteractionFields(fields, { isUpdate: true }),
        uploadingUrls,
        removingUrls,
      });
      return data;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['issueDetail', variables.id] });
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const deleteInteractionMutation = useMutation({
    mutationFn: async (id) => {
      const data = await deleteInteraction(id);
      return data;
    },
    onSuccess: async (_data, id) => {
      await queryClient.removeQueries({ queryKey: ['issueDetail', id] });
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  return {
    type: TrackerDataType.MEDIA_INTERACTION,
    newItemModalTitle: 'Add a New Media Interaction',
    createNewItem: async (fields, files, successCallback) => {
      const result = await createInteractionMutation.mutateAsync({ fields, files });
      if (successCallback && result?.id) {
        markCreated(result.id);
        successCallback({ postId: result.id });
      }
    },
    useListenOnCreation: (rowId) => {
      return { isNewlyAdded: isNewlyAdded(rowId) };
    },
    duplicateItem: async (id) => {
      await duplicateInteractionMutation.mutateAsync(id);
    },
    editItem: (val, index) => {
      let newVal;
      if (!val) {
        newVal = null;
      } else {
        switch (index) {
          case IssueKeys.type:
            {
              let array = [];
              val.forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case IssueKeys.linkedService:
            newVal = val.filter((item) => item !== null);
            break;
          case IssueKeys.lead:
            {
              let array = [];
              val.forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case IssueKeys.outlet:
            newVal = val._id;
            //TODO cancel journalist when outlet changed
            break;
          case IssueKeys.journalist:
            newVal = val._id;
            break;
          case IssueKeys.topic:
          case IssueKeys.response:
          case IssueKeys.keyMessaging:
          case IssueKeys.entryDate:
          case IssueKeys.dueDate:
          case IssueKeys.date:
            newVal = val;
            break;
          case IssueKeys.expert:
            newVal = val;
            break;
          case IssueKeys.units:
          case IssueKeys.campaign:
            let arr = [];
            val.forEach((obj) => arr.push(obj));
            newVal = arr;
            break;
          case IssueKeys.file:
            newVal = val;
            break;
          case IssueKeys.dept:
            newVal = val.map((c) => c.name);
            break;
          default:
            newVal = val.name;
            break;
        }
      }
      return newVal;
    },
    updateItemWithFile: async ({ id, fields }, _uploadingFiles, uploadingUrls, _removingFiles, removingUrls) => {
      try {
        await updateInteractionWithFileMutation.mutateAsync({
          id,
          fields,
          uploadingUrls,
          removingUrls,
        });
      } catch (err) {
        console.error(`failed to update issue with files - ${err}`);
      }
    },
    deleteItem: async (id) => {
      try {
        const data = await deleteInteractionMutation.mutateAsync(id);
        return data;
      } catch (err) {
        console.error(`failed to delete issue - ${err}`);
      }
    },
    flattenForExport: (data, dropdownTable) => {
      return data.map((row) => {
        try {
          let flatRow = JSON.parse(JSON.stringify(row.cells));
          // Rich text fields now come from lazy-loaded detail endpoints. Keeping them in CSV export would either
          // require exporting incomplete frontend summary data or reintroduce large payload issues for full-detail fetches.

          delete flatRow[IssueKeys.date];

          if (flatRow[IssueKeys.entryDate] && flatRow[IssueKeys.dueDate]) {
            flatRow['Duration'] = dayjs(flatRow[IssueKeys.dueDate]).diff(flatRow[IssueKeys.entryDate], 'day');
          }

          flatRow[IssueKeys.entryDate] = dayjs(flatRow[IssueKeys.entryDate]).format('YYYY-MM-DD');
          flatRow[IssueKeys.dueDate] = flatRow[IssueKeys.dueDate]
            ? dayjs(flatRow[IssueKeys.dueDate]).format('YYYY-MM-DD')
            : '';

          flatRow[IssueKeys.type] = flatRow[IssueKeys.type].map((c) => `${c.type}:${c.name}`).join(', \n');

          let obj;
          flatRow[IssueKeys.expert] = flatRow[IssueKeys.expert]
            .map((le) => dropdownTable['expert'].find((dropdownle) => dropdownle._id === le._id)?.name ?? '')
            .join(', \n');

          flatRow[IssueKeys.lead] = flatRow[IssueKeys.lead]
            .map((leadId) => dropdownTable['lead'].find((lead) => lead._id === leadId)?.name ?? null)
            .filter((leadName) => leadName !== null)
            .join(', ');

          flatRow[IssueKeys.linkedService] = flatRow[IssueKeys.linkedService].map((c) => c.name);

          obj = dropdownTable['journalist'].find((journalist) => journalist['_id'] === flatRow[IssueKeys.journalist]);
          flatRow[IssueKeys.journalist] = obj ? obj.name : null;
          flatRow['Journalist Email'] = obj ? obj.email : null;
          flatRow['Journalist Phone'] = obj ? obj.phone : null;

          obj = dropdownTable['outlet'].find((org) => org['_id'] === flatRow[IssueKeys.outlet]);
          flatRow[IssueKeys.outlet] = obj ? obj.name : null;

          flatRow[IssueKeys.file] = flatRow[IssueKeys.file].map((file) => {
            if (file.type === 'url') {
              return file.path;
            } else {
              return file.name;
            }
          });
          flatRow[IssueKeys.campaign] = flatRow[IssueKeys.campaign].map((campaign) => `${campaign.name}`).join(', \n');

          const contactMethodLabelMap = {
            'no intermediary': 'in person',
            phone: 'phone call',
          };
          const contactMethodValue = flatRow[IssueKeys.contactMethod];
          const contactMethodDisplay =
            contactMethodLabelMap[contactMethodValue] || contactMethodValue || '';
          flatRow = Object.keys(flatRow).reduce((acc, key) => {
            if (key === IssueKeys.contactMethod) {
              acc['Mode of Communication'] = contactMethodDisplay;
            } else {
              acc[key] = flatRow[key];
            }
            return acc;
          }, {});

          delete flatRow[IssueKeys.autotracker];
          delete flatRow[IssueKeys.fieldAddons];
          delete flatRow[IssueKeys.keyMessaging];
          delete flatRow[IssueKeys.response];

          return flatRow;
        } catch {
          return {
            [IssueKeys.entryDate]: '',
            [IssueKeys.dueDate]: '',
            'Mode of Communication': '',
            [IssueKeys.topic]: 'error flattening data for export!',
            [IssueKeys.type]: '',
            [IssueKeys.units]: '',
            [IssueKeys.expert]: '',
            [IssueKeys.outlet]: '',
            [IssueKeys.journalist]: '',
            'Journalist Email': '',
            'Journalist Phone': '',
            [IssueKeys.campaign]: '',
            [IssueKeys.lead]: '',
            [IssueKeys.file]: '',
            [IssueKeys.status]: '',
            [IssueKeys.dept]: '',
            [IssueKeys.linkedService]: '',
          };
        }
      });
    },
  };
};
